/**
 * Cross-field uniqueness: how one field's authored tag becomes the SET of peer
 * groups it belongs to.
 *
 * `uniquenessGroup` is authored as a COMMA-SEPARATED LIST so a single field can
 * sit in several independent peer sets at once. The rule that forced it is BMO's
 * RIF SIN triangle (tracker, 19 Oct — "Same SIN is acceptable for Investor,
 * spouse and successor"):
 *
 *   - Investor SIN  ≠ Spouse SIN
 *   - Investor SIN  ≠ Successor annuitant SIN
 *   - Spouse SIN    = Successor annuitant SIN is ALLOWED — the successor is the
 *     "Appointment of Spouse or Common Law Partner as Successor Annuitant", so on
 *     a spousal RIF they are normally the same person.
 *
 * One tag per field cannot express that. Putting all three in `rifSin` refuses the
 * spouse↔successor pair that business wants; leaving the successor out of the group
 * (the v30 state) lets the successor duplicate the INVESTOR. The shape the rule
 * actually has is two overlapping pairs, so the investor needs to be in two groups:
 * `rifSin` (with the spouse) and `rifSinSuccessor` (with the successor).
 *
 * A bare tag (`"rifSin"`) is just a one-element list, so every field authored before
 * this — and every frozen published version row carrying the old single-tag copy —
 * parses to exactly the behaviour it had.
 */

/**
 * How many groups one field may carry. Enforced by the domain on save and re-applied
 * here on read: a published version row freezes its own copy of every field setting,
 * and seeds / snapshots are hand-editable, so the runtime cannot assume the value it
 * is parsing ever passed the domain check.
 */
export const MAX_UNIQUENESS_GROUPS = 10;

/** Per-GROUP-NAME length cap (not the whole authored string). Mirrors the domain. */
export const MAX_UNIQUENESS_GROUP_NAME_LENGTH = 100;

/**
 * The longest authored `uniquenessGroup` string the domain will accept: ten
 * max-length names plus the nine commas between them. Used to size the builder's
 * input so the UI cannot produce a value the API would refuse.
 */
export const MAX_UNIQUENESS_GROUP_LENGTH =
  MAX_UNIQUENESS_GROUPS * MAX_UNIQUENESS_GROUP_NAME_LENGTH + (MAX_UNIQUENESS_GROUPS - 1);

/**
 * Parse an authored `uniquenessGroup` into the list of groups the field belongs to.
 *
 * Tokens are trimmed and blanks dropped (`"a, ,b"` → `['a','b']`), duplicates collapse
 * (a field in the same group twice is in it once), and the result is capped at
 * {@link MAX_UNIQUENESS_GROUPS}.
 *
 * Group names stay CASE-SENSITIVE. Live workflows carry both `signatoryEmail` and
 * `SignatoryEmail` as distinct groups today; folding case here would silently merge two
 * peer sets that have never been checked against each other. (Field VALUES are compared
 * case-insensitively — that's a separate decision, made in the uniqueness walker.)
 */
export function uniquenessGroupsOf(field: { uniquenessGroup?: string | null }): string[] {
  const raw = field.uniquenessGroup;
  if (!raw) return [];
  const groups: string[] = [];
  for (const token of raw.split(',')) {
    const group = token.trim();
    if (group.length === 0 || groups.includes(group)) continue;
    groups.push(group);
    if (groups.length === MAX_UNIQUENESS_GROUPS) break;
  }
  return groups;
}

/** Does this field participate in cross-field uniqueness at all? */
export function hasUniquenessGroup(field: { uniquenessGroup?: string | null }): boolean {
  return uniquenessGroupsOf(field).length > 0;
}

/** Do these two fields share at least one uniqueness group — i.e. are they peers? */
export function sharesUniquenessGroup(
  a: { uniquenessGroup?: string | null },
  b: { uniquenessGroup?: string | null },
): boolean {
  const groups = uniquenessGroupsOf(a);
  if (groups.length === 0) return false;
  return uniquenessGroupsOf(b).some((group) => groups.includes(group));
}
